import { CONFIGS, parseCSVWithConfig } from './erdosmiller/ErdosMillerParser';
import { HopTxSystemParser } from './erdosmiller/specialized/HopTxSystemParser';
import { VertexPulserParser } from './vertex/VertexPulserParser';
import { InnovaBatteryParser, isInnovaBatteryFile } from './innova/InnovaBatteryParser';
import { isProPulserFile, isProPulserFilename, ProPulserParser } from './prod/ProPulserParser';
import { DTechUnifiedParser } from './dtech/DTechUnifiedParser';
import { DTechFileDetector } from './dtech/utils/DTechFileDetector';
import { DTechXLSXHandler } from './dtech/utils/XLSXHandler';
import { PasonParser } from './pason/PasonParser';
import { JaeParser } from './jae/JaeParser';
import { EclipseTouchParser } from './eclipsetouch/EclipseTouchParser';
import { LASParser } from './las/LASParser';
import { GenericCsvParser } from './generic/GenericCsvParser';
import { DataQualityService } from '../services/DataQualityService';
import type { ParsedData, ToolType } from '../../interfaces/toolData';
import type { WaveFileType } from './types';
import { RimeParser } from './rime/RimeParser';
import { PhoenixVelocityParser } from './phx/PhoenixVelocityParser';

/**
 * Tool type detection result
 */
export interface ToolTypeDetectionResult {
  toolType: ToolType;
  confidence: number;
  method: 'filename' | 'content' | 'signature';
}

/**
 * File type detection result
 */
export interface FileTypeResult {
  type: WaveFileType | null
  confidence: number // 0-1 scale
  method: 'filename' | 'content' | 'column_validation'
  isValid: boolean
  errors: string[]
}

/**
 * File type detection options
 */
export interface FileTypeDetectionOptions {
  enableContentAnalysis?: boolean
  enableColumnValidation?: boolean
  maxLinesToRead?: number
  coreTypesOnly?: boolean // Only detect the 5 core types for parsing
}

/**
 * Parser Factory for multi-tool file detection and parsing
 * 
 * Supports:
 * - MicroPulse drilling tools (existing system)
 * - Innova battery diagnostic tools 
 * - Vertex pulser tools (new)
 * - Extensible for additional tool types
 */
export class ParserFactory {
  
  /**
   * Detect tool type from filename and content - STRICT MODE
   * Only returns a result if we can definitively identify the tool type
   */
  static detectToolType(filename: string, content: string): ToolTypeDetectionResult;
  static detectToolType(filename: string, buffer: ArrayBuffer): ToolTypeDetectionResult;
  static detectToolType(filename: string, contentOrBuffer: string | ArrayBuffer): ToolTypeDetectionResult {
    // Determine if we're dealing with XLSX or CSV based on file extension and content type
    const isXLSX = filename.toLowerCase().endsWith('.xlsx') || 
                   contentOrBuffer instanceof ArrayBuffer ||
                   (typeof contentOrBuffer === 'object' && contentOrBuffer !== null && 'byteLength' in contentOrBuffer);
    
    if (isXLSX) {
      return this.detectToolTypeFromXLSX(filename, contentOrBuffer as ArrayBuffer);
    } else {
      return this.detectToolTypeFromCSV(filename, contentOrBuffer as string);
    }
  }

  /**
   * Detect tool type from CSV content
   */
  private static detectToolTypeFromCSV(filename: string, content: string): ToolTypeDetectionResult {
    // 1. Check for Eclipse Touch decode export files (high confidence content signature)
    if (EclipseTouchParser.canParse(content)) {
      return {
        toolType: 'Eclipse Touch',
        confidence: 0.95,
        method: 'signature'
      };
    }
    
    // 2. Check for Pason EDR files (high confidence content signature)
    if (PasonParser.canParse(content)) {
      return {
        toolType: 'Pason',
        confidence: 0.95,
        method: 'signature'
      };
    }
    
    // 3. Check for DTech files using proper signature-based detection
    const dtechDetection = DTechFileDetector.detectFileType(content);
    if (dtechDetection.config && dtechDetection.confidence >= 0.7) {
      return {
        toolType: 'DTech',
        confidence: dtechDetection.confidence,
        method: 'signature'
      };
    }
    
    // 4. Check for Vertex pulser files (high confidence content signature)
    if (this.isVertexPulserFile(content)) {
      return {
        toolType: 'Vertex',
        confidence: 0.95,
        method: 'signature'
      };
    }
    
    // 5. Check for Innova battery files (high confidence content signature)
    if (isInnovaBatteryFile(content)) {
      return {
        toolType: 'Innova',
        confidence: 0.95,
        method: 'signature'
      };
    }
    
    // 5.1. Check for RIME Pulser Driver files (content signature)
    if (RimeParser.canParse(content)) {
      return {
        toolType: 'RIME',
        confidence: 0.95,
        method: 'signature'
      };
    }
    
    // 5.2 Phoenix Velocity (EVO) content signature
    if (PhoenixVelocityParser.canParse(content)) {
      return {
        toolType: 'Phoenix',
        confidence: 0.95,
        method: 'signature'
      };
    }

    // 6. Check for Pro Pulser files (filename pattern + content signature)
    if (isProPulserFilename(filename)) {
      return {
        toolType: 'ProPulser',
        confidence: 0.95,
        method: 'filename'
      };
    }
    
    // Pro Pulser content-based detection
    if (isProPulserFile(content)) {
      return {
        toolType: 'ProPulser',
        confidence: 0.9,
        method: 'signature'
      };
    }
    
    // 7. Check for MicroPulse files (exact filename pattern required)
    if (this.isMicroPulseFile(filename)) {
      return {
        toolType: 'MicroPulse', 
        confidence: 0.95,
        method: 'filename'
      };
    }
    
    // 8. Check for Cortex files (exact filename pattern required)
    if (this.isCortexFile(filename)) {
      return {
        toolType: 'Cortex',
        confidence: 0.95,
        method: 'filename'
      };
    }
    
    // 9. Check for HopRx files (exact filename pattern required)
    if (this.isHopRxFile(filename)) {
      return {
        toolType: 'HopRx',
        confidence: 0.95,
        method: 'filename'
      };
    }
    
    // 10. Check for HopTx files (exact filename pattern required)
    if (this.isHopTxFile(filename)) {
      return {
        toolType: 'HopTx',
        confidence: 0.95,
        method: 'filename'
      };
    }

    // 11. Check for HASS files (filename or content-based)
    // Prefer filename hint when available, with robust header-based fallback
    if (filename.includes('HASS_') || this.isHassContent(content)) {
      return {
        toolType: 'HASS',
        confidence: filename.includes('HASS_') ? 0.98 : 0.95,
        method: filename.includes('HASS_') ? 'filename' : 'content'
      };
    }
    
    // 12. Check for LAS (Log ASCII Standard) files (high confidence content signature)
    if (LASParser.canParse(content)) {
      return {
        toolType: 'LAS',
        confidence: 0.95,
        method: 'signature'
      };
    }
    
    // 13. Content-based detection for JAE Smart DM files
    if (JaeParser.canParse(content)) {
      return {
        toolType: 'JAE',
        confidence: 0.9,
        method: 'content'
      };
    }
    
    // 14. Content-based fallback for MicroPulse files when filename detection fails
    if (this.detectMicroPulseFromContent(content)) {
      return {
        toolType: 'MicroPulse',
        confidence: 0.8,
        method: 'content'
      };
    }
    
    // 15. Generic CSV fallback - try before throwing error
    if (GenericCsvParser.canParse(content)) {
      return {
        toolType: 'Generic',
        confidence: 0.5, // Lower confidence as it's a fallback
        method: 'content'
      };
    }
    
    // NO FALLBACKS - if we can't definitively identify it, throw an error
    throw new Error(`Unrecognized file format. Cannot determine tool type for file: ${filename}`);
  }

  /**
   * Detect tool type from XLSX content
   */
  private static detectToolTypeFromXLSX(filename: string, buffer: ArrayBuffer): ToolTypeDetectionResult {
    // Currently only D-Tech files support XLSX format
    // Extract headers from XLSX to determine if it's a D-Tech file
    try {
      // Validate XLSX format first
      if (!DTechXLSXHandler.isValidXLSX(buffer)) {
        throw new Error(`Invalid XLSX file format: ${filename}`);
      }

      // Extract headers for D-Tech detection
      const headers = DTechXLSXHandler.extractHeaders(buffer);
      const headerString = headers.join(','); // Create CSV-like header string for detection
      
      // Create fake CSV content with header + dummy data row for detection
      const fakeCSVContent = headerString + '\n' + headers.map(() => 'dummy').join(',');
      
      // Use proper signature-based D-Tech detection
      const dtechDetection = DTechFileDetector.detectFileType(fakeCSVContent);
      
      if (dtechDetection.config && dtechDetection.confidence >= 0.7) {
        return {
          toolType: 'DTech',
          confidence: dtechDetection.confidence,
          method: 'signature'
        };
      }

      // No other parsers currently support XLSX
      throw new Error(`XLSX format not supported for this file type. Only D-Tech XLSX files are currently supported: ${filename}`);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to detect XLSX file type: ${errorMessage}`);
    }
  }
  
  /**
   * Parse file using appropriate parser based on detected tool type
   */
  static parseFile(filename: string, content: string): ParsedData;
  static parseFile(filename: string, buffer: ArrayBuffer): ParsedData;
  static parseFile(filename: string, contentOrBuffer: string | ArrayBuffer): ParsedData {
    // Determine if we're dealing with XLSX or CSV
    const isXLSX = filename.toLowerCase().endsWith('.xlsx') || contentOrBuffer instanceof ArrayBuffer;
    
    // Call the appropriate overload for detection
    const detection = isXLSX 
      ? this.detectToolType(filename, contentOrBuffer as ArrayBuffer)
      : this.detectToolType(filename, contentOrBuffer as string);
    
    // Parse using appropriate parser
    let rawData: ParsedData;
    switch (detection.toolType) {
      case 'Phoenix':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for Phoenix Velocity files: ${filename}`);
        }
        rawData = PhoenixVelocityParser.parse(contentOrBuffer as string, filename);
        break;
      case 'RIME':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for RIME files: ${filename}`);
        }
        rawData = RimeParser.parse(contentOrBuffer as string, filename);
        break;
      case 'Eclipse Touch':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for Eclipse Touch files: ${filename}`);
        }
        rawData = EclipseTouchParser.parse(contentOrBuffer as string);
        break;
        
      case 'Pason':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for Pason files: ${filename}`);
        }
        rawData = PasonParser.parse(contentOrBuffer as string);
        break;

      case 'LAS':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for LAS files: ${filename}`);
        }
        rawData = LASParser.parse(contentOrBuffer as string);
        break;
        
      case 'DTech':
        // Use signature-based detection to find the appropriate config
        let dtechDetection;
        if (isXLSX) {
          // For XLSX files, extract headers and use them for detection
          const headers = DTechXLSXHandler.extractHeaders(contentOrBuffer as ArrayBuffer);
          const headerString = headers.join(',');
          // Create fake CSV content for detection (detectFileType expects multi-line content)
          const fakeCSVContent = headerString + '\n' + headers.map(() => 'dummy').join(',');
          dtechDetection = DTechFileDetector.detectFileType(fakeCSVContent);
        } else {
          // For CSV files, use content directly
          dtechDetection = DTechFileDetector.detectFileType(contentOrBuffer as string);
        }
        
        if (!dtechDetection.config) {
          throw new Error(`Could not determine D-Tech file type. Detection confidence too low or no matching signature found.`);
        }
        
        console.log(`🔍 Detected D-Tech format: ${dtechDetection.config.fileType} (confidence: ${(dtechDetection.confidence * 100).toFixed(1)}%)`);
        
        const dtechParser = new DTechUnifiedParser();
        if (isXLSX) {
          rawData = dtechParser.parseXLSX(contentOrBuffer as ArrayBuffer, filename, dtechDetection.config);
        } else {
          rawData = dtechParser.parseCSV(contentOrBuffer as string, filename, dtechDetection.config);
        }
        break;
        
      case 'Vertex':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for Vertex files: ${filename}`);
        }
        const vertexParser = new VertexPulserParser();
        rawData = vertexParser.parse(contentOrBuffer as string, filename);
        break;
        
      case 'Innova':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for Innova files: ${filename}`);
        }
        const innovaParser = new InnovaBatteryParser();
        rawData = innovaParser.parse(contentOrBuffer as string, filename);
        break;
        
      case 'ProPulser':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for ProPulser files: ${filename}`);
        }
        const proPulserParser = new ProPulserParser();
        rawData = proPulserParser.parse(contentOrBuffer as string, filename);
        break;
        
      case 'MicroPulse':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for MicroPulse files: ${filename}`);
        }
        // Use direct MicroPulse parsing with filename + content fallback
        rawData = this.parseMicroPulse(filename, contentOrBuffer as string);
        break;
        
      case 'Cortex':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for Cortex files: ${filename}`);
        }
        // Use direct Cortex parsing - no detectFileType() needed
        rawData = this.parseCortex(filename, contentOrBuffer as string);
        break;
        
      case 'HopRx':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for HopRx files: ${filename}`);
        }
        // Use direct HopRx parsing - no detectFileType() needed
        rawData = this.parseHopRx(filename, contentOrBuffer as string);
        break;
        
      case 'HopTx':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for HopTx files: ${filename}`);
        }
        // Use direct HopTx parsing - no detectFileType() needed
        rawData = this.parseHopTx(filename, contentOrBuffer as string);
        break;

      case 'HASS':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for HASS files: ${filename}`);
        }
        rawData = this.parseHass(filename, contentOrBuffer as string);
        break;
        
      case 'JAE':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for JAE files: ${filename}`);
        }
        rawData = JaeParser.parse(contentOrBuffer as string, filename);
        break;
        
      case 'Generic':
        if (isXLSX) {
          throw new Error(`XLSX format not supported for Generic CSV files: ${filename}`);
        }
        rawData = GenericCsvParser.parse(contentOrBuffer as string, filename);
        break;
        
      default:
        throw new Error(`Unsupported tool type: ${detection.toolType}`);
    }

    // Apply centralized data quality checks
    return DataQualityService.clean(rawData);
  }

  /**
   * Detect MicroPulse file type from content when filename detection fails
   */
  private static detectMicroPulseFromContent(content: string): WaveFileType | null {
    // Must start with MicroPulse signature
    if (!content.includes('MicroPulse')) {
      return null;
    }
    
    // MicroPulse content patterns - check more specific patterns first
    if (content.includes('Shock_vibe Log')) return 'EM_MICROPULSE_SHOCK_VIBE';
    if (content.includes('Continuous_meas Log')) return 'EM_MICROPULSE_CONTINUOUS_MEAS';
    if (content.includes('Shock Log') || content.includes('SHOCK Log')) return 'EM_MICROPULSE_SHOCK';
    
    // Standard MicroPulse content patterns - check both uppercase and capitalized
    if (content.includes('FLOW Log') || content.includes('Flow Log')) return 'EM_MICROPULSE_FLOW';
    if (content.includes('PULSE Log') || content.includes('Pulse Log')) return 'EM_MICROPULSE_PULSE';
    if (content.includes('Rotgamma Log') || content.includes('ROTGAMMA Log')) return 'EM_MICROPULSE_ROTGAMMA';
    if (content.includes('SURVEY Log') || content.includes('Survey Log')) return 'EM_MICROPULSE_SURVEY';
    if (content.includes('VIBE Log') || content.includes('Vibe Log')) return 'EM_MICROPULSE_VIBE';
    if (content.includes('Debug Log') || content.includes('DEBUG Log')) return 'EM_MICROPULSE_DEBUG';
    if (content.includes('Gamma Log') || content.includes('GAMMA Log')) return 'EM_MICROPULSE_GAMMA';
    if (content.includes('Temp Log') || content.includes('TEMP Log')) return 'EM_MICROPULSE_TEMP';
    if (content.includes('Voltages Log') || content.includes('VOLTAGES Log')) return 'EM_MICROPULSE_VOLTAGES';
    if (content.includes('Route Log') || content.includes('ROUTE Log')) return 'EM_MICROPULSE_ROUTE';
    if (content.includes('System Log') || content.includes('SYSTEM Log')) return 'EM_MICROPULSE_SYSTEM';
    if (content.includes('Telem Log') || content.includes('TELEM Log')) return 'EM_MICROPULSE_TELEM';
    if (content.includes('Config Log') || content.includes('CONFIG Log')) return 'EM_MICROPULSE_CONFIG';
    if (content.includes('Rss Log') || content.includes('RSS Log')) return 'EM_MICROPULSE_RSS';
    
    return null; // Not a MicroPulse file
  }

  /**
   * Parse MicroPulse files directly using appropriate config - handles both filename and content detection
   */
  private static parseMicroPulse(filename: string, content: string): ParsedData {
    let fileType: string | null = null;
    
    // Try filename-based detection first
    if (filename.includes('_SHOCK_VIBE_')) fileType = 'EM_MICROPULSE_SHOCK_VIBE';
    else if (filename.includes('_SHOCK_')) fileType = 'EM_MICROPULSE_SHOCK';
    else if (filename.includes('_CONTINUOUS_MEAS_')) fileType = 'EM_MICROPULSE_CONTINUOUS_MEAS';
    else if (filename.includes('_FLOW_')) fileType = 'EM_MICROPULSE_FLOW';
    else if (filename.includes('_PULSE_')) fileType = 'EM_MICROPULSE_PULSE';
    else if (filename.includes('_ROTGAMMA_')) fileType = 'EM_MICROPULSE_ROTGAMMA';
    else if (filename.includes('_SURVEY_')) fileType = 'EM_MICROPULSE_SURVEY';
    else if (filename.includes('_VIBE_')) fileType = 'EM_MICROPULSE_VIBE';
    else if (filename.includes('_DEBUG_')) fileType = 'EM_MICROPULSE_DEBUG';
    else if (filename.includes('_GAMMA_')) fileType = 'EM_MICROPULSE_GAMMA';
    else if (filename.includes('_TEMP_')) fileType = 'EM_MICROPULSE_TEMP';
    else if (filename.includes('_VOLTAGES_')) fileType = 'EM_MICROPULSE_VOLTAGES';
    else if (filename.includes('_ROUTE_')) fileType = 'EM_MICROPULSE_ROUTE';
    else if (filename.includes('_SYSTEM_')) fileType = 'EM_MICROPULSE_SYSTEM';
    else if (filename.includes('_TELEM_')) fileType = 'EM_MICROPULSE_TELEM';
    else if (filename.includes('_CONFIG_')) fileType = 'EM_MICROPULSE_CONFIG';
    else if (filename.includes('_RSS_')) fileType = 'EM_MICROPULSE_RSS';
    
    // Fall back to content-based detection if filename didn't work
    if (!fileType) {
      fileType = this.detectMicroPulseFromContent(content);
    }
    
    if (!fileType) {
      throw new Error(`Unsupported MicroPulse file type. Filename: ${filename}`);
    }
    
    // Map new EM_ prefixed file types to old config keys
    const configKey = fileType.replace('EM_MICROPULSE_', 'MICROPULSE_');
    const config = CONFIGS[configKey as keyof typeof CONFIGS];
    if (!config) {
      throw new Error(`No configuration found for file type: ${fileType} (config key: ${configKey})`);
    }
    
    return parseCSVWithConfig(content, config, filename);
  }

  /**
   * Parse Cortex files directly using appropriate config
   */
  private static parseCortex(filename: string, content: string): ParsedData {
    // Direct filename-based routing for Cortex files - no detectFileType() needed
    // Check more specific patterns first to avoid incorrect matches
    if (filename.includes('_GYRO_CONTINUOUS_MEAS_')) {
      return parseCSVWithConfig(content, CONFIGS.CORTEX_GYRO_CONTINUOUS_MEAS, filename);
    }
    if (filename.includes('_GYRO_SURVEY_')) {
      return parseCSVWithConfig(content, CONFIGS.CORTEX_GYRO_SURVEY, filename);
    }
    if (filename.includes('_RAWSUR_') && filename.includes('_SEC_F3_')) {
      return parseCSVWithConfig(content, CONFIGS.CORTEX_RAWSUR_SEC, filename);
    }
    if (filename.includes('_RAWSUR_') && filename.includes('_F3_')) {
      return parseCSVWithConfig(content, CONFIGS.CORTEX_RAWSUR_F3, filename);
    }
    if (filename.includes('_SURVEY_SECONDARY_')) {
      return parseCSVWithConfig(content, CONFIGS.CORTEX_SURVEY_SECONDARY, filename);
    }
    if (filename.includes('_SURVEY_')) {
      return parseCSVWithConfig(content, CONFIGS.CORTEX_SURVEY, filename);
    }
    if (filename.includes('_SYSTEM_')) {
      return parseCSVWithConfig(content, CONFIGS.CORTEX_SYSTEM, filename);
    }
    if (filename.includes('_VOLTAGES_')) {
      return parseCSVWithConfig(content, CONFIGS.CORTEX_VOLTAGES, filename);
    }
    
    // Add more Cortex file types here as they are implemented
    // if (filename.includes('_SYSTEM_')) return parseCSVWithConfig(content, CONFIGS.CORTEX_SYSTEM);
    // if (filename.includes('_VOLTAGES_')) return parseCSVWithConfig(content, CONFIGS.CORTEX_VOLTAGES);
    
    throw new Error(`Unsupported Cortex file type. Filename: ${filename}`);
  }

  /**
   * Parse HopRx files directly using appropriate config
   */
  private static parseHopRx(filename: string, content: string): ParsedData {
    // Direct filename-based routing for HopRx files - no detectFileType() needed
    if (filename.includes('_ICRUISE_')) {
      return parseCSVWithConfig(content, CONFIGS.HOPRX_ICRUISE, filename);
    }
    if (filename.includes('_VOLTAGES_')) {
      return parseCSVWithConfig(content, CONFIGS.HOPRX_VOLTAGES, filename);
    }
    if (filename.includes('_RXLOG_')) {
      // Special handling for RXLOG files which are comment-based error messages, not CSV data
      return this.parseHopRxRxlog(filename, content);
    }
    
    throw new Error(`Unsupported HopRx file type. Filename: ${filename}`);
  }

  /**
   * Special parser for HopRx RXLOG files which contain error messages, not CSV data
   */
  private static parseHopRxRxlog(_filename: string, content: string): ParsedData {
    const lines = content.split('\n').filter(line => line.trim() !== '');
    
    // Create minimal parsed data structure for error message files
    const errorData: ParsedData = {
      toolType: 'HopRx',
      fileType: 'EM_HOPRX_RXLOG',
      curves: [{
        name: 'Error Message',
        xValues: lines.map((_, index) => new Date(Date.now() + index * 1000)), // Dummy timestamps
        yValues: lines.map((_, index) => index), // Line numbers as values
        yUnit: ''
      }],
      metadata: {
        'File Type': 'RXLOG Parsing Error',
        'Content Type': 'Error Messages',
        'Lines': lines.length.toString()
      },
      dataPreview: {
        first5: lines.slice(0, 5).map(line => ({ 'Error Message': line })),
        last5: lines.slice(-5).map(line => ({ 'Error Message': line })),
        totalCount: lines.length
      },
      errors: []
    };
    
    return errorData;
  }

  /**
   * Parse HopTx files directly using appropriate config
   */
  private static parseHopTx(filename: string, content: string): ParsedData {
    // Direct filename-based routing for HopTx files - no detectFileType() needed
    if (filename.includes('_SYSTEM_')) {
      return this.parseHopTxSystemFile(content, filename);
    }
    if (filename.includes('_TX_')) {
      return parseCSVWithConfig(content, CONFIGS.HOPTX_TX, filename);
    }
    if (filename.includes('_VOLTAGES_')) {
      return parseCSVWithConfig(content, CONFIGS.HOPTX_VOLTAGES, filename);
    }
    
    throw new Error(`Unsupported HopTx file type. Filename: ${filename}`);
  }

  /**
   * Parse HopTx SYSTEM files with proper event handling
   */
  private static parseHopTxSystemFile(content: string, filename: string): ParsedData {
    return HopTxSystemParser.parse(content, filename);
  }

  /**
   * Parse HASS logger files
   */
  private static parseHass(_filename: string, content: string): ParsedData {
    // Single-file-type product for now
    return parseCSVWithConfig(content, CONFIGS.HASS_DEFAULT, _filename);
  }



  // Private helper methods for tool type detection
  
  private static isMicroPulseFile(filename: string): boolean {
    // MicroPulse files follow specific naming: MicroPulse_{ID}_{TYPE}_{TIMESTAMP}.csv
    return filename.startsWith('MicroPulse_');
  }
  
  private static isCortexFile(filename: string): boolean {
    // Cortex files must follow specific naming pattern: Cortex_{MODEL}_{ID}_{TYPE}_{TIMESTAMP}.csv
    // Must have at least 4 underscore-separated parts after 'Cortex_'
    if (!filename.startsWith('Cortex_') || !filename.endsWith('.csv')) {
      return false;
    }
    const withoutPrefixAndSuffix = filename.slice(7, -4); // Remove 'Cortex_' and '.csv'
    const parts = withoutPrefixAndSuffix.split('_');
    // Require at least 3 parts after 'Cortex_': MODEL, ID, TYPE (and optionally TIMESTAMP/other parts)
    return parts.length >= 3 && parts.every(part => part.length > 0);
  }
  
  private static isHopRxFile(filename: string): boolean {
    // HopRx files must follow specific naming pattern: HopRx_{ID}_{TYPE}_{TIMESTAMP}.csv
    // Must have at least 3 underscore-separated parts after 'HopRx_'
    if (!filename.startsWith('HopRx_') || !filename.endsWith('.csv')) {
      return false;
    }
    const withoutPrefixAndSuffix = filename.slice(6, -4); // Remove 'HopRx_' and '.csv'
    const parts = withoutPrefixAndSuffix.split('_');
    // Require at least 3 parts after 'HopRx_': ID, TYPE, TIMESTAMP
    return parts.length >= 3 && parts.every(part => part.length > 0);
  }

  private static isHopTxFile(filename: string): boolean {
    // HopTx files must follow specific naming pattern: HopTx_{ID}_{TYPE}_{TIMESTAMP}.csv
    // Must have at least 3 underscore-separated parts after 'HopTx_'
    if (!filename.startsWith('HopTx_') || !filename.endsWith('.csv')) {
      return false;
    }
    const withoutPrefixAndSuffix = filename.slice(6, -4); // Remove 'HopTx_' and '.csv'
    const parts = withoutPrefixAndSuffix.split('_');
    // Require at least 3 parts after 'HopTx_': ID, TYPE, TIMESTAMP
    return parts.length >= 3 && parts.every(part => part.length > 0);
  }

  /**
   * Check if content appears to be a Vertex pulser log file
   * Vertex files don't have reliable filename patterns, so detection must be content-based
   */
  private static isVertexPulserFile(content: string): boolean {
    // Check for characteristic Vertex headers
    if (content.includes('Date/Time') && 
        content.includes('Close ms') && 
        content.includes('Open ms') && 
        content.includes('Pulse Width ms')) {
      return true;
    }
    
    // Check for Vertex firmware version string
    if (content.includes('Vertex Pulser Firmware Version')) {
      return true;
    }
    
    // Check for characteristic timestamp pattern
    if (/\+\s*\d+:\d+:\d+:\d+\.\d+/.test(content)) {
      return true;
    }
    
    return false;
  }

  /**
   * Detect HASS content by distinctive headers
   */
  private static isHassContent(content: string): boolean {
    const headerLine = content.split('\n').find(l => l.includes(',')) || '';
    const header = headerLine.toLowerCase();
    // Core HASS identifiers seen in real logger output
    const hasCore = header.includes('calculated_azimuth') &&
                    header.includes('calculated_inclination') &&
                    header.includes('calibrated_ax');
    if (!hasCore) return false;

    // Distinguish from other formats using characteristic extras:
    // - dual time columns: 'timestamp' and 'tool_timestamp'
    // - raw magnetometer axes 'raw_mx', 'raw_my', 'raw_mz' (or alt accel naming seen in some dumps)
    const hasDualTime = header.includes('timestamp,') && header.includes('tool_timestamp');
    const hasRawMag = header.includes('raw_mx') || header.includes('raw_my') || header.includes('raw_mz');
    const hasAltRawAccel = header.includes('raw_accel_1_x'); // allow mocked variant used in tests

    return hasDualTime && (hasRawMag || hasAltRawAccel);
  }
}
